import { TIndustryForm, TIndustryRequirement } from "@/features/learning-content/types/industryForm";
import { TIndustryRequirementDto } from "@/api/services/admin/learning-content/dtos/industry-requirement.dto";
import { TExamCreateForm, TSubjectDetailInfo } from "@/features/learning-content/types/learningContent";
import { TCreateIndustryReqDto } from "@/api/services/admin/learning-content/dtos/create-industry.dto";
import { TCreateExamReqDto } from "@/api/services/admin/learning-content/dtos/create-exam.dto";
import { TUpdateExamReqDto } from "@/api/services/admin/learning-content/dtos/update-exam.dto";
import { TUpdateIndustryReqDto } from "@/api/services/admin/learning-content/dtos/update-industry.dto";
import { TGetExamDetailDto } from "@/api/services/admin/learning-content/dtos/get-exam-detail.dto";
import { TGetIndustryDto } from "@/api/services/admin/learning-content/dtos/get-industry.dto";
import { TGetSubjectDetailDto } from "@/api/services/admin/learning-content/dtos/get-subject-detail.dto";
import { DATE_FORMAT, DATE_NOT_TIMEZONE_FORMAT } from "@/constants/datetime";
import { dayjs } from "@/utils/dayjs";

// 受験要件: number of requirement boxes always rendered in the form (要件1/2/3)
const REQUIREMENT_BOX_COUNT = 3;

// 受験要件: drop fully-empty boxes, keep only entries with a 項目名,
// tagging each kept entry with its 1-based box position so its slot survives update/delete
const convertRequirementsToReq = (requirements?: TIndustryRequirement[]): TIndustryRequirementDto[] =>
  requirements
    ?.map((req, index) => ({ req, index }))
    .filter(({ req }) => req.name?.trim())
    .map(({ req, index }) => ({
      ...(req.id != null && { id: req.id }),
      name: req.name ?? null,
      description: req.description ?? null,
      displayOrder: index + 1,
    })) ?? [];

// 受験要件: map API entries back to form by 1-based displayOrder (fallback to array position for legacy data without it)
const convertRequirementsResToForm = (requirements?: TIndustryRequirementDto[]): TIndustryRequirement[] => {
  const hasDisplayOrder = requirements?.some((req) => req.displayOrder != null);

  return Array.from({ length: REQUIREMENT_BOX_COUNT }, (_, index) => {
    const req = hasDisplayOrder ? requirements?.find((item) => item.displayOrder === index + 1) : requirements?.[index];

    return {
      id: req?.id,
      name: req?.name ?? null,
      description: req?.description ?? null,
      hasCheckedLearners: req?.hasCheckedLearners ?? false,
    };
  });
};

export const convertIndustryFormToCreateReq = (data: TIndustryForm): TCreateIndustryReqDto => {
  return {
    name: data.name,
    isActive: data.isActive ?? false,
    activationDate: data.activationDate?.format(DATE_NOT_TIMEZONE_FORMAT) ?? null,
    internalMemo: data.internalMemo ?? null,
    examType: (data.examType as TCreateIndustryReqDto["examType"]) ?? undefined,
    examOrganization: data.examOrganization ?? null,
    examInfoReleaseEstimate: data.examInfoReleaseEstimate ?? null,
    examInfoUrl: data.examInfoUrl ?? null,
    nextExamDate: data.nextExamDate?.format(DATE_NOT_TIMEZONE_FORMAT) ?? null,
    displayOrder: data.displayOrder ?? 0,
    examRequirements: convertRequirementsToReq(data.examRequirements),
  };
};

export const convertIndustryFormToUpdateReq = (data: TIndustryForm): TUpdateIndustryReqDto => {
  return {
    name: data.name,
    isActive: data.isActive ?? false,
    activationDate: data.activationDate?.format(DATE_NOT_TIMEZONE_FORMAT) ?? null,
    internalMemo: data.internalMemo ?? null,
    examType: (data.examType as TUpdateIndustryReqDto["examType"]) ?? undefined,
    examOrganization: data.examOrganization ?? null,
    examInfoReleaseEstimate: data.examInfoReleaseEstimate ?? null,
    examInfoUrl: data.examInfoUrl ?? null,
    nextExamDate: data.nextExamDate?.format(DATE_NOT_TIMEZONE_FORMAT) ?? null,
    displayOrder: data.displayOrder ?? 0,
    examRequirements: convertRequirementsToReq(data.examRequirements),
  };
};

export const convertIndustryResToForm = (data: TGetIndustryDto): Partial<TIndustryForm> => {
  return {
    name: data.name,
    isActive: data.isActive,
    activationDate: data.activationDate ? dayjs(data.activationDate) : null,
    internalMemo: data.internalMemo ?? null,
    examType: data.examType,
    examOrganization: data.examOrganization ?? null,
    examInfoReleaseEstimate: data.examInfoReleaseEstimate ?? null,
    examInfoUrl: data.examInfoUrl ?? null,
    nextExamDate: data.nextExamDate ? dayjs(data.nextExamDate) : null,
    displayOrder: data.displayOrder,
    examRequirements: convertRequirementsResToForm(data.examRequirements),
  };
};

// Map frontend form fields to backend API fields
export const convertExamFormToCreateReq = (data: TExamCreateForm, industryId: number): TCreateExamReqDto => {
  return {
    industryId,
    periodName: data.periodName,
    status: data.status,
    examMethod: data.examMethod,
    examMonth: data.examMonth?.format(DATE_NOT_TIMEZONE_FORMAT) ?? "",
    examOverview: data.examOverview ?? null,
    examInfoSource: data.examInfoSource ?? null,
    internalMemo: data.internalMemo ?? null,
    relatedLinks: data.relatedLinks?.filter((link) => link.label && link.url).map((link) => ({ label: link.label, url: link.url })) ?? [],
    procedures:
      data.procedures?.map((proc) => ({
        agent: proc.agent,
        site: proc.site ?? null,
        stepName: proc.stepName,
        description: proc.description ?? null,
        duration: proc.duration ?? null,
        startDate: null,
        endDate: proc.deadline?.format(DATE_NOT_TIMEZONE_FORMAT) ?? null,
      })) ?? [],
    examDayDescription: data.examDayDescription ?? null,
    passFailDescription: data.passFailDescription ?? null,
    announcementEstimate: data.announcementEstimate ?? null,
  };
};

export const convertExamFormToUpdateReq = (data: TExamCreateForm): TUpdateExamReqDto => {
  return {
    periodName: data.periodName,
    status: data.status,
    examMethod: data.examMethod,
    examMonth: data.examMonth?.format(DATE_NOT_TIMEZONE_FORMAT) ?? "",
    examOverview: data.examOverview ?? null,
    examInfoSource: data.examInfoSource ?? null,
    internalMemo: data.internalMemo ?? null,
    relatedLinks:
      data.relatedLinks
        ?.filter((link) => link.label && link.url)
        .map((link) => ({ ...(link.id != null && { id: link.id }), label: link.label, url: link.url })) ?? [],
    procedures:
      data.procedures?.map((proc) => ({
        ...(proc.id != null && { id: proc.id }),
        agent: proc.agent,
        site: proc.site ?? null,
        stepName: proc.stepName,
        description: proc.description ?? null,
        duration: proc.duration ?? null,
        startDate: null,
        endDate: proc.deadline?.format(DATE_NOT_TIMEZONE_FORMAT) ?? null,
      })) ?? [],
    examDayDescription: data.examDayDescription ?? null,
    passFailDescription: data.passFailDescription ?? null,
    announcementEstimate: data.announcementEstimate ?? null,
  };
};

// Convert subject detail API response to display info for SubjectInfoCard
export const convertSubjectDetailToInfo = (data: TGetSubjectDetailDto, industryName?: string): TSubjectDetailInfo => {
  return {
    id: data.id,
    categoryName: industryName ?? "",
    subjectCode: data.partType ?? "",
    subjectName: data.name,
    status: data.isActive ? "enabled" : "disabled",
    activationDate: data.activationDate ? dayjs(data.activationDate).format(DATE_FORMAT) : "----/--/--",
    registeredDate: data.createdAt ? dayjs(data.createdAt).format(DATE_FORMAT) : "",
    internalNote: data.internalMemo ?? "",
  };
};

// Map backend API response to frontend form fields
export const convertExamDetailToForm = (data: TGetExamDetailDto): TExamCreateForm => {
  return {
    periodName: data.periodName,
    status: data.status,
    examMethod: data.examMethod,
    examMonth: data.examMonth ? dayjs(data.examMonth) : null,
    examOverview: data.examOverview ?? "",
    examInfoSource: data.examInfoSource ?? "",
    internalMemo: data.internalMemo ?? "",
    relatedLinks: data.relatedLinks?.length
      ? data.relatedLinks.map((link) => ({ id: Number(link.id), label: link.label, url: link.url }))
      : [{ label: "", url: "" }],
    procedures: data.procedures?.length
      ? data.procedures.map((proc) => ({
          id: Number(proc.id),
          agent: proc.agent,
          site: proc.site ?? null,
          stepName: proc.stepName,
          description: proc.description ?? null,
          duration: proc.duration ?? null,
          deadline: proc.endDate ? dayjs(proc.endDate) : null,
          hasAssignedLearners: proc.hasAssignedLearners,
        }))
      : [{ agent: "", site: null, stepName: "", description: null, duration: null, deadline: null }],
    examDayDescription: data.examDayDescription ?? "",
    passFailDescription: data.passFailDescription ?? "",
    announcementEstimate: data.announcementEstimate ?? "",
  };
};
